import { useEffect, useMemo, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { US_PREVIEW } from "@/lib/geo/us-preview";
import { loadCountyShapes, type CountyShape } from "@/lib/geo/county-shapes";
import { boundsOfPath } from "@/lib/map/view";
import { frameFor } from "@/lib/county-map";
import {
  CDC_STATUSES,
  CDC_STATUS_LABEL,
  CDC_STATUS_TONE,
  CDC_STATUS_VAR,
  statusIn,
  type CdcCountyStatus,
  type StateCountyStatuses,
} from "@/lib/county-status-text";
import { TonePattern } from "./map/tone-pattern";
import { cn } from "@/lib/utils";

/** Width the figure is drawn at on a phone or in the card's side column, for mark sizes. */
const DRAWN_WIDTH_PX = 320;

/**
 * Fig. 1 on a county page: every county in the state in the colour of its CDC record,
 * with this one outlined in ink. It answers "and what about the places around me?" at a
 * glance, which a sentence about one county cannot.
 *
 * The state's outline is drawn on the server from the small outline set the home page
 * already uses, so the figure holds its space and reads as a map before the county
 * shapes (one lazy chunk per state, shared by every county page in it) arrive.
 * Clicking a county opens its page; the same links are in the "Nearby" list for keyboard
 * and screen-reader users.
 */
export function CountyMap({
  stateAbbr,
  stateSlug,
  stateName,
  fips,
  label,
  nouns,
  lists,
  year,
  className,
}: {
  stateAbbr: string;
  stateSlug: string;
  stateName: string;
  /** The county to outline; leave out on a state page, where no county is picked. */
  fips?: string;
  label?: string;
  /** "counties" or "parishes". */
  nouns: string;
  lists: StateCountyStatuses;
  year: number;
  className?: string;
}) {
  const router = useRouter();
  const [shapes, setShapes] = useState<CountyShape[] | null>(null);

  useEffect(() => {
    let live = true;
    loadCountyShapes(stateAbbr).then((s) => live && setShapes(s));
    return () => {
      live = false;
    };
  }, [stateAbbr]);

  const outline = US_PREVIEW.find((g) => g.abbr === stateAbbr)?.d ?? "";
  const frame = useMemo(() => frameFor(boundsOfPath(outline), DRAWN_WIDTH_PX), [outline]);
  const u = frame.unit;
  const self = fips ? shapes?.find((s) => s.fips === fips) : undefined;
  const status = fips ? statusIn(lists, fips) : null;
  const counts: Record<CdcCountyStatus, number> = {
    established: lists.established.length,
    reported: lists.reported.length,
    "no-records": lists.total - lists.established.length - lists.reported.length,
  };
  const patternId = (s: CdcCountyStatus) => `cdc-county-${s}`;

  function open(e: React.MouseEvent<SVGSVGElement>) {
    const slug = (e.target as Element).closest("[data-slug]")?.getAttribute("data-slug");
    if (slug && slug !== self?.slug) {
      router.navigate({ to: "/states/$state/$county", params: { state: stateSlug, county: slug } });
    }
  }

  return (
    <figure className={cn("min-w-0", className)}>
      <div className="relative w-full" style={{ aspectRatio: String(frame.aspect) }}>
        <svg
          viewBox={frame.viewBox}
          role="img"
          aria-label={
            label && status
              ? `Map of ${stateName} ${nouns}, coloured by CDC's ${year} record. ${label} is outlined: ${CDC_STATUS_LABEL[status]}.`
              : `Map of ${stateName} ${nouns}, coloured by CDC's ${year} record: ${counts.established} established, ${counts.reported} reported, ${counts["no-records"]} with no records.`
          }
          className="absolute inset-0 h-full w-full"
          onClick={open}
        >
          <defs>
            {CDC_STATUSES.map((s) => (
              <TonePattern
                key={s}
                id={patternId(s)}
                tone={CDC_STATUS_TONE[s]}
                fill={CDC_STATUS_VAR[s]}
                ink="var(--foreground)"
                size={9 * u}
                line={1.05 * u}
              />
            ))}
          </defs>
          {!shapes && (
            <path
              d={outline}
              fill="color-mix(in oklab, var(--foreground) 7%, transparent)"
              stroke="color-mix(in oklab, var(--foreground) 35%, transparent)"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          )}
          {shapes?.map((s) => {
            const st = statusIn(lists, s.fips);
            return (
              <path
                key={s.fips}
                d={s.d}
                data-slug={s.slug}
                fill={`url(#${patternId(st)})`}
                stroke="var(--card)"
                strokeWidth={0.6}
                vectorEffect="non-scaling-stroke"
                className={s.fips === fips ? undefined : "cursor-pointer hover:opacity-75"}
              >
                <title>{`${s.name}: ${CDC_STATUS_LABEL[st]}`}</title>
              </path>
            );
          })}
          {self && (
            <>
              <path
                d={self.d}
                fill="none"
                stroke="var(--card)"
                strokeWidth={4.5}
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
              <path
                d={self.d}
                fill="none"
                stroke="var(--foreground)"
                strokeWidth={2.25}
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            </>
          )}
        </svg>
        {!shapes && (
          <p className="absolute inset-x-0 bottom-0 text-center text-xs text-muted-foreground">
            Loading {nouns}…
          </p>
        )}
      </div>
      <figcaption className="mt-3 text-xs leading-relaxed text-muted-foreground">
        <span className="font-semibold text-foreground">Fig. 1.</span>{" "}
        {label ? (
          <>
            {label}, outlined in ink, among {stateName}&rsquo;s {lists.total} {nouns}, by
            CDC&rsquo;s {year} record.
          </>
        ) : (
          <>
            {stateName}&rsquo;s {lists.total} {nouns}, by CDC&rsquo;s {year} record. Click one to
            open its page.
          </>
        )}
      </figcaption>
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-foreground">
        {[...CDC_STATUSES].reverse().map((s) => (
          <li key={s} className="inline-flex items-center gap-1.5">
            <svg aria-hidden viewBox="0 0 12 12" className="h-3 w-3 shrink-0">
              <defs>
                <TonePattern
                  id={`key-${patternId(s)}`}
                  tone={CDC_STATUS_TONE[s]}
                  fill={CDC_STATUS_VAR[s]}
                  ink="var(--foreground)"
                  size={6}
                  line={1}
                />
              </defs>
              <rect
                width="12"
                height="12"
                fill={`url(#key-${patternId(s)})`}
                stroke="color-mix(in oklab, var(--foreground) 35%, transparent)"
              />
            </svg>
            {CDC_STATUS_LABEL[s]}
            <span className="tabular-nums text-muted-foreground">{counts[s]}</span>
          </li>
        ))}
      </ul>
    </figure>
  );
}
